import type {
  OfferEvaluation,
} from "../../domain/offer-evaluation";
import {
  formatDisplayDate,
  formatEvaluationAmount,
  type PurchaseRequest,
} from "../../model";

export function formatScoreMetric(value: number) {
  return value.toLocaleString("tr-TR", { maximumFractionDigits: 1 });
}

function joinTurkish(items: string[]) {
  if (items.length < 2) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} ve ${items.at(-1)}`;
}

export function offerScoreExplanation(
  offer: OfferEvaluation["offers"][number],
  evaluation: OfferEvaluation,
) {
  if (
    offer.totalScore === null ||
    offer.amountScore === null ||
    offer.deliveryScore === null ||
    offer.paymentScore === null
  ) {
    return "Puan kırılımı bu teklif için hesaplanamadı.";
  }

  const criteria = [
    {
      label: "Toplam maliyet",
      score: offer.amountScore,
      weight: evaluation.weights.amount,
    },
    {
      label: "Teslimat",
      score: offer.deliveryScore,
      weight: evaluation.weights.delivery,
    },
    {
      label: "Vade",
      score: offer.paymentScore,
      weight: evaluation.weights.payment,
    },
  ];
  const strengths = criteria
    .filter((criterion) => criterion.score >= 90)
    .map(
      (criterion) =>
        `${criterion.label} (${formatScoreMetric(criterion.score)}/100)`,
    );
  const strongest = [...criteria].sort(
    (left, right) => right.score - left.score || right.weight - left.weight,
  )[0];
  const mainWeakness = criteria
    .filter((criterion) => criterion.score < 50)
    .sort((left, right) => right.weight - left.weight)[0];
  const lines = [
    strengths.length
      ? `Güçlü yönler: ${joinTurkish(strengths)}.`
      : `En güçlü alan: ${strongest.label} (${formatScoreMetric(strongest.score)}/100).`,
  ];
  if (mainWeakness) {
    lines.push(
      `Puanı düşüren: ${mainWeakness.label} (%${mainWeakness.weight} ağırlık, ${formatScoreMetric(mainWeakness.score)}/100).`,
    );
  }
  const performanceWeight = evaluation.categoryProfile?.performanceWeight;
  lines.push(
    performanceWeight && typeof offer.performanceScore === "number"
      ? `Sonuç: Geçmiş performansın %${performanceWeight} etkisiyle ${formatScoreMetric(offer.totalScore)}/100.`
      : `Sonuç: ${formatScoreMetric(offer.totalScore)}/100.`,
  );
  return lines.join("\n");
}

export function buildCorrectionDraft(
  fields: string[],
  offer: OfferEvaluation["offers"][number] | null | undefined,
  evaluation: OfferEvaluation | null | undefined,
  request: PurchaseRequest,
) {
  if (fields.length === 0) return "";

  const supplier = offer?.supplier ?? "Seçili tedarikçi";
  const currency = evaluation?.comparisonCurrency ?? offer?.currency ?? "TRY";
  const instructions = fields.map((field) => {
    switch (field) {
      case "Teklif fiyatı":
        return offer?.unitPrice !== null && offer?.unitPrice !== undefined
          ? `${supplier} teklifindeki ${formatEvaluationAmount(offer.unitPrice, currency)} birim fiyat tedarikçiyle yeniden görüşülsün ve güncel fiyat teklife işlensin.`
          : `${supplier} teklifinin birim fiyatı tedarikçiyle yeniden görüşülsün ve güncel fiyat teklife işlensin.`;
      case "Toplam bedel": {
        const total =
          offer?.comparableTotal ?? offer?.enteredTotal ?? offer?.amount;
        return total !== null && total !== undefined
          ? `${formatEvaluationAmount(total, currency)} toplam bedelin tüm maliyet kalemlerini içerdiği doğrulansın ve güncel toplam kaydedilsin.`
          : "Teklifin toplam bedeli tüm maliyet kalemleriyle birlikte doğrulansın ve güncel toplam kaydedilsin.";
      }
      case "Teslimat süresi":
        return `${offer?.deliveryDays ?? "Kayıtlı olmayan"} günlük teslim süresi, ${formatDisplayDate(request.due)} ihtiyaç tarihi dikkate alınarak yeniden teyit edilsin ve güncel teslim taahhüdü kaydedilsin.`;
      case "Ödeme vadesi":
        return `${offer?.paymentDays ?? "Kayıtlı olmayan"} günlük ödeme vadesi tedarikçiyle yeniden görüşülsün ve mutabık kalınan güncel vade teklife işlensin.`;
      case "Ek maliyetler":
        return "Navlun, sigorta, kurulum ve diğer ek maliyetler ayrı ayrı doğrulanarak toplam bedele eksiksiz yansıtılsın.";
      case "Tedarikçi":
        return `${supplier} için kategori uygunluğu, ticari yeterlilik ve güncel tedarikçi bilgileri doğrulanarak dosyaya kaydedilsin.`;
      case "Belgeler":
        return "Teklif ve tedarikçi belgeleri güncel, doğrulanabilir ve teklif koşullarıyla tutarlı biçimde dosyaya eklensin.";
      default:
        return `${field} konusu doğrulanarak dosyada gerekli güncelleme yapılsın.`;
    }
  });

  return `${instructions.join(" ")} Yapılan değişiklikler açıkça belirtilerek dosya yeniden onaya sunulsun.`;
}
